import { setStringAsync } from 'expo-clipboard';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  useColorScheme,
  View,
  ViewStyle,
} from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import vxlColors from 'shared/styles/color/core';
import theme from 'shared/styles/theme';
import { formatDate } from 'shared/utils/string/date';

export interface ChatBubbleProps {
  isTyping: boolean;
  position: 'right' | 'left';
  createdAt: string;
  hideTimeStamp?: boolean;
  children: ReactNode;
  isDisabled?: boolean;
  htmlAsString?: string;
}

const handleBubbleStyle = (position): ViewStyle | undefined => {
  if (position === 'left') {
    return styles.bubbleLeft;
  } else if (position === 'right') {
    return styles.bubbleRight;
  }
  return undefined;
};

const handleBubbleTextStyle = (
  position: 'right' | 'left',
  isDisabled?: boolean,
) => {
  if (position === 'left' && !isDisabled) {
    return styles.leftBubbleText;
  }
  return styles.rightBubbleText;
};

const handleTooltiptStyle = (position: 'right' | 'left') => {
  if (position === 'left') {
    return [styles.tooltipPosition, { left: 1 }];
  }
  return [styles.tooltipPosition, { right: 1 }];
};

const ChatBubble = ({
  createdAt,
  position,
  isTyping,
  hideTimeStamp = false,
  children,
  isDisabled,
  htmlAsString,
}: ChatBubbleProps) => {
  const [toolTipVisible, setToolTipVisible] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const { t: translatedText } = useTranslation('chatStrings', {
    keyPrefix: 'chat',
  });
  const colorScheme = useColorScheme();
  const backgroundColor =
    colorScheme === 'dark'
      ? vxlColors.core.neutrals.black
      : theme.color.background.primary;
  const textColor =
    colorScheme === 'dark'
      ? theme.color.background.primary
      : vxlColors.core.neutrals.black;

  const arrowBorder =
    colorScheme === 'dark' ? '' : theme.color.background.primary;

  const handleLongPress = () => {
    setToolTipVisible(true);
    setIsFocusedRemoved(true);
  };

  const handleCopy = (data?: string | ReactNode) => {
    setToolTipVisible(false);
    void setStringAsync((data as string) || '');
  };

  useEffect(() => {
    if (toolTipVisible) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
    } else {
      fadeAnim?.setValue(0);
    }
  }, [fadeAnim, toolTipVisible]);

  const [isFocusedRemoved, setIsFocusedRemoved] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null;

    if (isFocusedRemoved) {
      timeout = setTimeout(() => {
        setIsFocusedRemoved(false);
        setToolTipVisible(false);
      }, 2000);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isFocusedRemoved]);

  return (
    <View>
      <View
        style={[
          styles.chatBubble,
          handleBubbleStyle(position),
          isTyping && styles.isTyping,
          isDisabled && styles.isDisabled,
        ]}
        testID="messageBubble"
      >
        <TouchableOpacity onLongPress={handleLongPress}>
          {typeof children === 'string' ? (
            <BodyBaseText
              style={handleBubbleTextStyle(position, isDisabled)}
              numberOfLines={0}
            >
              {children}
            </BodyBaseText>
          ) : (
            children
          )}

          {!hideTimeStamp ? (
            <BodyXSText
              style={[styles.time, handleBubbleTextStyle(position, isDisabled)]}
              numberOfLines={0}
            >
              {formatDate(createdAt, 'hh:mm aaa')}
            </BodyXSText>
          ) : null}
        </TouchableOpacity>
      </View>
      {toolTipVisible ? (
        <Pressable
          onPress={() => {
            handleCopy(htmlAsString?.replace(/<[^>]+>/g, '') || children);
          }}
          style={handleTooltiptStyle(position)}
        >
          <Animated.View
            style={[
              styles.tooltip,
              { opacity: fadeAnim, backgroundColor: backgroundColor },
            ]}
          >
            <BodyBaseText style={{ color: textColor }}>
              {translatedText('copyText')}
            </BodyBaseText>
          </Animated.View>
          <View
            style={[
              styles.tooltipArrow,
              position === 'left' ? styles.arrowLeft : styles.arrowRight,
              { borderTopColor: arrowBorder },
            ]}
          />
        </Pressable>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  arrowLeft: {
    left: 10,
  },

  arrowRight: {
    right: 10,
  },

  bubbleLeft: {
    backgroundColor: theme.color.border.focused,
    borderTopLeftRadius: 0,
    borderTopRightRadius: theme.spacing.s,
    marginRight: 'auto',
  },

  bubbleRight: {
    backgroundColor: theme.color.border.focusedInverse,
    borderTopLeftRadius: theme.spacing.s,
    borderTopRightRadius: 0,
    marginLeft: 'auto',
  },

  chatBubble: {
    alignSelf: 'flex-start',
    borderBottomLeftRadius: theme.spacing.s,
    borderBottomRightRadius: theme.spacing.s,
    display: 'flex',
    gap: theme.spacing.s,
    marginVertical: theme.spacing.s,
    maxWidth: 280,
    minWidth: theme.spacing.xl,
    padding: 12,
    zIndex: 10,
  },

  isDisabled: {
    backgroundColor: theme.color.background.secondary,
  },

  isTyping: {
    borderTopLeftRadius: theme.spacing.s,
    borderTopRightRadius: theme.spacing.s,
  },

  leftBubbleText: {
    color: theme.color.background.primary,
    height: 'auto',
  },
  rightBubbleText: {
    color: theme.color.interactive.black,
    height: 'auto',
  },
  time: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
  tooltip: {
    borderRadius: theme.spacing.s,
    paddingHorizontal: 12,
    paddingVertical: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.45,
    shadowRadius: 4,
  },
  tooltipArrow: {
    borderLeftColor: 'transparent',
    borderLeftWidth: 6,
    borderRightColor: 'transparent',
    borderRightWidth: 6,
    borderTopWidth: 6,
    height: 0,
    position: 'absolute',
    top: '100%',
    width: 0,
  },
  tooltipPosition: {
    position: 'absolute',
    top: 0,
    zIndex: 30,
  },
});

export default ChatBubble;
